// API Types based on OpenAPI schema

export type UserRole = 'ADMIN' | 'REGULAR';

export type UserState = 'ACTIVE' | 'NON_ACTIVE';

export type RelationshipType = 
  | 'COACH_TRAINEE' 
  | 'TEACHER_STUDENT' 
  | 'MENTOR_MENTEE' 
  | 'CONSULTANT_CLIENT' 
  | 'THERAPIST_CLIENT';

export type UserRelationStatus = 
  | 'PENDING_APPROVAL_BY_COACH' 
  | 'PENDING_APPROVAL_BY_TRAINEE' 
  | 'ACTIVE' 
  | 'DECLINED_BY_COACH' 
  | 'DECLINED_BY_TRAINEE' 
  | 'INACTIVE';

export type PlanCode = 'FREE' | 'TRIAL' | 'SOLO' | 'PRO';

export interface FeatureEntitlements {
  workoutMode: boolean;
  workoutHistory: boolean;
  proView: boolean;
  clientWorkoutHistory: boolean;
  autoProlongation: boolean;
  createOwnBlocksAndPrograms: boolean;
  canAddTrainee: boolean;
  hasActiveCoach: boolean;
}

export interface User {
  id: number;
  chatId?: number;
  username?: string;
  firstName?: string;
  lastName?: string;
  photoUrl?: string;
  role: UserRole;
  state: UserState;
  hasTrainees: boolean;
  hasCoaches: boolean;
  isVirtual?: boolean;
  planCode?: PlanCode;
  entitlements?: FeatureEntitlements;
  trialAvailable?: boolean;
}

export interface UserRelation {
  id: number;
  username?: string;
  firstName?: string;
  lastName?: string;
  photoUrl?: string;
  role: UserRole;
  state: UserState;
  coachId: number;
  relationStatus: UserRelationStatus;
  isVirtual?: boolean;
}

export interface CreateRelationRequest {
  userId: number;
  inviteeUsername: string;
  inviteMessage?: string;
}

export interface CreateVirtualTraineeRequest {
  firstName: string;
  lastName?: string;
}

export interface UpdateVirtualTraineeRequest {
  firstName?: string;
  lastName?: string;
}

export interface CreateRelationResponse {
  success: boolean;
  relation?: UserRelation;
  error?: string;
}

export interface CoachStatistics {
  activeTrainees: number;
  thisWeekTrainings: number;
  todayTrainings: number;
  pendingApprovals: number;
  canBeCompleted: number;
  totalActiveTrainingSets: number;
}

export interface TraineeStatistics {
  thisWeekTrainings: number;
  todayTrainings: number;
  totalCompletedTrainings: number;
  currentStreak: number;
  nextTraining: string | null;
  closestTrainingSetId: number | null;
  closestTrainingSetCoachId: number | null;
  closestTrainingSetEndDate: string | null;
  closestTrainingSetRemaining: number | null;
  activeTrainingSets: number;
}

export interface UserStatistics {
  coachStats: CoachStatistics;
  traineeStats: TraineeStatistics;
}


export type DayOfWeek = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';

export interface TrainDay {
  day: DayOfWeek;
  startTime?: string | null; // ISO time format HH:mm:ss or null
}

export interface UserScheduleEntry {
  day: DayOfWeek;
  startTime: string; // "HH:mm"
}

export interface TrainingSetCreateRequest {
  traineeId: number;
  days: TrainDay[];
  startDate: string; // ISO date format YYYY-MM-DD
  plannedTrainings: number;
  duration: string; // ISO 8601 duration format, e.g., "PT1H30M"
  maxSkipCount?: number;
}

export type TrainingSetStatus = 'DRAFT' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED';

export type TrainingProjectionStatus = 'ACTIVE' | 'CANCELLED' | 'MOVED' | 'SKIPPED';

export interface TrainingProjection {
  id: number | null;
  setId: number | null;
  start: string;
  end: string;
  status: TrainingProjectionStatus;
  comment: string | null;
  isFired: boolean | null;
  displayStatus?: string; // Time-based logical status: COMPLETED, UPCOMING, or ACTIVE
}

export interface TrainingSet {
  id: number;
  coachId: number;
  traineeId: number;
  days: TrainDay[];
  startDate: string;
  calculatedEndDate: string;
  plannedTrainings: number;
  status: TrainingSetStatus;
  duration: number; // Duration in seconds
  maxSkipCount?: number;
  pauseStartDate?: string | null; // ISO date format YYYY-MM-DD
  pauseEndDate?: string | null; // ISO date format YYYY-MM-DD
  /** true = unpaid (needs attention); null/false = no attention */
  unpaid?: boolean | null;
}

export interface TrainingSetWithUser {
  trainingSet: TrainingSet;
  relatedUser: UserRelation; // The "other" user (coach if current user is trainee, trainee if current user is coach)
  canBeCompleted: boolean; // True if all trainings are in the past
  isCurrentUserCoach: boolean; // True if current user is the coach in this relationship
}

export interface UpdateTrainingRequest {
  status?: TrainingProjectionStatus;
  newStartTime?: string; // ISO datetime format
  newDuration?: string; // ISO 8601 duration format, e.g., "PT1H30M"
  updateReason?: string;
  fired?: boolean;
}

export interface RelatedUser {
  id: number;
  username?: string;
  firstName?: string;
  lastName?: string;
  photoUrl?: string;
  relationStatus: UserRelationStatus;
}

export interface TrainingSetStats {
  plannedCount: number;
  completedCount: number; // Completed trainings (not fired)
  completedFiredCount: number; // Completed fired trainings
  futureCount: number; // Future trainings (not fired)
  futureFiredCount: number; // Future fired trainings
  totalFiredCount: number; // Total fired trainings
  skippedCount: number; // Skipped trainings (not fired)
  cancelledCount: number; // Cancelled trainings (not fired)
  totalCompleted: number; // completedCount + completedFiredCount
  totalRemaining: number; // futureCount + futureFiredCount
}

export interface TrainingSetDetailsResponse {
  relatedUser: RelatedUser;
  trainingSet: TrainingSet;
  trainProjections: TrainingProjection[];
  stats: TrainingSetStats;
}

export interface ThisWeekTraining {
  projectionId: number;
  setId: number;
  start: string; // ISO datetime
  end: string; // ISO datetime
  status: string;
  otherUserId: number;
  otherUserFirstName: string | null;
  otherUserLastName: string | null;
  otherUserUsername: string | null;
  otherUserPhotoUrl?: string | null;
  isCoach: boolean;
}

// Training Set Template Types
export interface TrainingSetTemplate {
  id: number;
  name: string;
  description?: string;
  defaultStartTime?: string; // ISO time format HH:mm:ss
  plannedTrainings?: number;
  duration?: number; // Duration in seconds
  maxSkipCount?: number;
  days?: TrainDay[];
  createdAt: string;
  updatedAt: string;
}

export interface TrainingSetTemplateCreateRequest {
  name: string;
  description?: string;
  defaultStartTime?: string; // ISO time format HH:mm:ss
  plannedTrainings?: number;
  duration?: number; // Duration in seconds
  maxSkipCount?: number;
  days?: TrainDay[];
}

export interface TrainingSetTemplateUpdateRequest {
  id: number;
  name?: string;
  description?: string;
  defaultStartTime?: string; // ISO time format HH:mm:ss
  plannedTrainings?: number;
  duration?: number; // Duration in seconds
  maxSkipCount?: number;
  days?: TrainDay[];
}

export interface UserPreferences {
  receiverTypes?: RelationshipType[];
  onboardingCompleted?: boolean;
  notifyMyLastTrainingLeft?: boolean;
  notifyTraineesLastTrainingLeft?: boolean;
  notifyTrainingsCanBeCompleted?: boolean;
  photoSyncEnabled?: boolean;
  pinnedShortcuts?: string[];
  /** Map of tour id -> completed version (e.g. { coach: 3, proView: 1 }). */
  tmaTourProgress?: Record<string, number>;
}

export interface UpdateUserPreferencesRequest {
  receiverTypes?: RelationshipType[];
  notifyMyLastTrainingLeft?: boolean;
  notifyTraineesLastTrainingLeft?: boolean;
  notifyTrainingsCanBeCompleted?: boolean;
  photoSyncEnabled?: boolean;
  pinnedShortcuts?: string[];
  /** Partial map merged into existing tour progress. */
  tmaTourProgress?: Record<string, number>;
}

// Training Block Types
export interface TrainingBlockItemExercise {
  id?: number;
  name: string;
  sortOrder?: number;
}

export interface TrainingBlockItem {
  id?: number;
  sortOrder?: number;
  note?: string | null;
  exercises: TrainingBlockItemExercise[];
}

/** @deprecated Use TrainingBlockItem */
export type TrainingBlockExercise = TrainingBlockItemExercise & {
  sets?: number;
  reps?: number;
  weight?: string;
};

export interface TrainingBlock {
  id: number;
  userId: number;
  name: string;
  tags: string[];
  items: TrainingBlockItem[];
  isSaved?: boolean;
  createdAt: string;
}

// Training Program Types
export interface TrainingProgramDay {
  id: number;
  sortOrder: number;
  blockId: number;
  blockName?: string;
  blockTags?: string[];
  blockIsSaved?: boolean;
  items?: TrainingBlockItem[];
}

export interface TrainingProgram {
  id: number;
  userId: number;
  name: string;
  tags?: string[];
  days: TrainingProgramDay[];
  isActive: boolean;
  isSaved?: boolean;
  createdAt: string;
}

export interface CreateTrainingProgramRequest {
  name: string;
  tags?: string[];
  blockIds: number[];
  isSaved?: boolean;
}

export interface TrainingProgramActivation {
  id: number;
  userId: number;
  programId: number;
  sourceProgramId?: number | null;
  programName: string;
  programIsSaved?: boolean;
  durationWeeks: number;
  startedAt: string;
  endedAt: string | null;
  isActive: boolean;
  assignedByCoachId?: number | null;
}

export function activationMatchesTemplateProgram(
  activation: TrainingProgramActivation,
  templateProgramId: number,
): boolean {
  return activation.programId === templateProgramId || activation.sourceProgramId === templateProgramId
}

export interface AssignTrainingProgramRequest {
  traineeId: number;
  durationWeeks: number;
  /** ISO date YYYY-MM-DD. Defaults to today on the server when omitted. */
  startDate?: string;
}

export interface AssignOnDemandDayNewBlock {
  name: string;
  tags?: string[];
  items: {
    note?: string | null;
    exercises: { name: string }[];
  }[];
}

export interface AssignOnDemandDaySpec {
  blockId?: number;
  newBlock?: AssignOnDemandDayNewBlock;
}

export interface AssignOnDemandTrainingProgramRequest {
  traineeId: number;
  durationWeeks: number;
  startDate?: string;
  /** Optional. Server defaults to trainee name + start date. */
  programName?: string;
  tags?: string[];
  days: AssignOnDemandDaySpec[];
}

export interface ProgramProjection {
  id: number;
  activationId: number;
  blockId: number;
  blockName: string;
  blockTags: string[];
  start: string;
  weekNumber: number;
  status: 'ACTIVE' | 'CANCELLED' | 'SKIPPED';
}
